import React, { memo, useCallback, useMemo, useRef } from 'react';
import { motion, useReducedMotion, LayoutGroup } from 'framer-motion';
import type { CategoryFilterProps } from './types';

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories = [],
  selectedCategory,
  onCategoryChange,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);

  /* -------------------------------
     Safe + stable category list
  --------------------------------*/
  const allCategories = useMemo(() => {
    const unique = new Set(['all', ...categories]);
    return Array.from(unique);
  }, [categories]);

  /* -------------------------------
     Animation spring
  --------------------------------*/
  const spring = useMemo(
    () =>
      prefersReducedMotion
        ? { duration: 0 }
        : {
            type: 'spring' as const,
            stiffness: 420,
            damping: 34,
            mass: 0.7,
          },
    [prefersReducedMotion]
  );

  /* -------------------------------
     Change handler
  --------------------------------*/
  const handleCategoryClick = useCallback(
    (category: string, el?: HTMLButtonElement | null) => {
      if (category !== selectedCategory) {
        onCategoryChange(category);

        // Auto scroll selected tab into view (Premium UX)
        el?.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    },
    [selectedCategory, onCategoryChange]
  );

  /* -------------------------------
     Keyboard navigation (Accessibility)
  --------------------------------*/
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (!containerRef.current) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();

        const nextIndex =
          e.key === 'ArrowRight'
            ? (index + 1) % allCategories.length
            : (index - 1 + allCategories.length) % allCategories.length;

        const buttons =
          containerRef.current.querySelectorAll<HTMLButtonElement>('button');

        buttons[nextIndex]?.focus();
        buttons[nextIndex]?.click();
      }
    },
    [allCategories.length]
  );

  return (
    <LayoutGroup>
      <div
        ref={containerRef}
        role="tablist"
        aria-label="Category Filter"
        className="
          mb-[clamp(12px,2vw,24px)]
          flex
          overflow-x-auto
          scroll-smooth
          snap-x snap-mandatory
          gap-x-[clamp(14px,2.4vw,28px)]
          gap-y-3
          border-b-2 border-gray-200 dark:border-gray-700
          scrollbar-none
        "
      >
        {allCategories.map((category, index) => {
          const isActive = selectedCategory === category;
          const label = category === 'all' ? 'All' : category;

          return (
            <button
              key={category}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              aria-label={`Filter ${label}`}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onClick={(e) =>
                handleCategoryClick(category, e.currentTarget)
              }
              className="
                relative
                snap-center
                flex items-center
                pb-[clamp(6px,1vw,10px)]
                px-1
                min-h-[40px]
                focus:outline-none
                focus-visible:ring-2
                focus-visible:ring-primary/40
                rounded-sm
                group
                select-none
                will-change-transform
              "
            >
              {/* TEXT */}
              <motion.span
                layout
                transition={spring}
                className={`
                  font-semibold
                  whitespace-nowrap
                  leading-tight
                  transition-colors duration-200
                  
                  /* TRUE fluid typography */
                  text-[clamp(0.72rem,0.58rem+0.38vw,1.05rem)]

                  ${
                    isActive
                      ? 'text-primary'
                      : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200'
                  }
                `}
              >
                {label}
              </motion.span>

              {/* UNDERLINE */}
              {isActive && (
                <motion.div
                  layoutId="category-underline"
                  transition={spring}
                  className="
                    absolute
                    left-0
                    right-0
                    bottom-0
                    h-[2px]
                    bg-primary
                    rounded-full
                  "
                />
              )}
            </button>
          );
        })}
      </div>
    </LayoutGroup>
  );
};

export default memo(CategoryFilter);
